import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import type { Seat } from '@basgan/rules';
import type { PublicSeat } from '@basgan/protocol';

interface Props {
  seats: PublicSeat[];
  /**
   * Sıralama — maç sonu olayından geliyor, görünümden değil.
   *
   * Görünüm animasyon kuyruğunun gerisinden geliyor ve panel açıldığında son
   * elin puanları henüz işlenmemiş oluyordu: tablo bir an herkesi 0 puanla
   * gösteriyordu.
   */
  standings: { seat: Seat; score: number; place: number }[];
  me: Seat;
  /** Masadan kalkanlar, geliş sırasıyla. */
  sold: { seat: Seat; name: string }[];
  /** Masada kalmayı seçen koltuklar. */
  stayed: Seat[];
  /** Kararın son anı (epoch ms). Botlara karşı oynarken null — kimse beklemiyor. */
  decideBy: number | null;
  onStay(): void;
  onSell(): void;
}

/**
 * Maç sonu: "ee şimdi napıyonuz?"
 *
 * Maç bitince masa DAĞILMIYOR. Herkes kalırsa aynı masada yeni maç geri
 * sayımı başlıyor; kalkan olursa koltuğu boşalıyor ve masa yeni oyuncuya
 * (ya da yöneticinin ekleyeceği bota) açılıyor.
 *
 * Karar süreli: kararını vermeyen tek bir oyuncu, kalan üçünü masada sonsuza
 * kadar bekletirdi. Süre dolunca sunucu o koltuğu satmış sayıyor — buradaki
 * geri sayım aynı sürenin görünen yüzü.
 */
export function MatchOver({ seats, standings, me, sold, stayed, decideBy, onStay, onSell }: Props) {
  const seconds = useCountdown(decideBy);
  /** Kararımı verdim mi — sunucudan dönmesini beklemeden düğmeler kapansın. */
  const [decided, setDecided] = useState(false);

  const soldSeats = new Set(sold.map((s) => s.seat));
  const nameOf = (seat: Seat) => seats.find((s) => s.seat === seat);
  const order = [...standings].sort((a, b) => a.place - b.place);

  return (
    <div className="overlay">
      <section className="dialog">
        <header className="dialog__head">
          <h3>MAÇ BİTTİ</h3>
          <span className="dialog__sub">16 el · 4 set</span>
        </header>

        <table className="board-table">
          <tbody>
            {order.map((row) => {
              const info = nameOf(row.seat);
              return (
                <tr key={row.seat} className={row.seat === me ? 'is-me' : undefined}>
                  <td className={`place place--${row.place}`}>{row.place}</td>
                  <td className="board-table__name">
                    {info?.name ?? '—'}
                    {(info?.botMoves ?? 0) > 0 && (
                      <span className="tag tag--bot">{info!.botMoves} hamle bot</span>
                    )}
                    {stayed.includes(row.seat) && <span className="tag tag--stay">kalıyor</span>}
                    {soldSeats.has(row.seat) && <span className="tag tag--off">kalktı</span>}
                  </td>
                  <td className="board-table__score">{row.score}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Duyurular tablonun ALTINDA: satırın içine sıkıştırıldığında adlar
            kırpılıyordu ve asıl esprili kısım görünmüyordu. */}
        {sold.length > 0 && (
          <ul className="matchover__sold">
            {sold.map((s) => (
              <motion.li
                key={s.seat}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {s.name} Şerefsizi Sattı
              </motion.li>
            ))}
          </ul>
        )}

        <p className="matchover__ask">
          ee şimdi napıyonuz?
          {seconds !== null && !decided && <span className="matchover__clock">{seconds}</span>}
        </p>

        {decided ? (
          /* Kararımı verdim: masa şimdi diğerlerini bekliyor. Düğmeleri açık
             bırakmak "acaba gitti mi?" sorusu yaratıyordu. */
          <p className="matchover__wait">
            Masada kaldın — diğerleri bekleniyor{seconds !== null ? ` (${seconds})` : ''}
          </p>
        ) : (
          <div className="actions">
            <button
              className="btn btn--pill btn--danger"
              type="button"
              onClick={() => {
                setDecided(true);
                onSell();
              }}
            >
              Sat ve git
            </button>
            <button
              className="btn btn--primary btn--pill"
              type="button"
              onClick={() => {
                setDecided(true);
                onStay();
              }}
            >
              Kal oyna
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

/** Saniye cinsinden geri sayım; hedef yoksa null. */
function useCountdown(target: number | null): number | null {
  const left = () => (target === null ? null : Math.max(0, Math.ceil((target - Date.now()) / 1000)));
  const [seconds, setSeconds] = useState(left);

  useEffect(() => {
    setSeconds(left());
    if (target === null) return;
    const id = setInterval(() => setSeconds(left()), 250);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return seconds;
}
